/** Private, in-memory CAD bytes. Every reuse is revalidated by the authorized asset route. */
export const CAD_ASSET_CACHE_MAX_BYTES = 128 * 1024 * 1024
export const CAD_ASSET_CACHE_MAX_ENTRIES = 3
export const CAD_ASSET_CACHE_TTL_MS = 15 * 60 * 1000

export type CadAssetIdentity = {
    accountScope?: string
    fileId: string
    generation?: string
    assetId: string
}
export type CadAssetResponse = { status: number; bytes: ArrayBuffer; etag?: string }
type Entry = { bytes: ArrayBuffer; etag: string; expiresAt: number }
type Options = { maxBytes?: number; maxEntries?: number; ttlMs?: number; now?: () => number }

export class CadAssetCache {
    private readonly entries = new Map<string, Entry>()
    private readonly maxBytes: number
    private readonly maxEntries: number
    private readonly ttlMs: number
    private readonly now: () => number
    private retainedBytes = 0
    private epoch = 0

    constructor(options: Options = {}) {
        this.maxBytes = options.maxBytes ?? CAD_ASSET_CACHE_MAX_BYTES
        this.maxEntries = options.maxEntries ?? CAD_ASSET_CACHE_MAX_ENTRIES
        this.ttlMs = options.ttlMs ?? CAD_ASSET_CACHE_TTL_MS
        this.now = options.now ?? Date.now
    }

    private remove(key: string): void {
        const entry = this.entries.get(key)
        if (!entry) return
        this.entries.delete(key)
        this.retainedBytes -= entry.bytes.byteLength
    }

    clear(): void {
        this.epoch += 1
        this.entries.clear()
        this.retainedBytes = 0
    }

    async load(
        identity: CadAssetIdentity,
        fetcher: (etag?: string) => Promise<CadAssetResponse>,
        signal?: AbortSignal
    ): Promise<ArrayBuffer> {
        const startedEpoch = this.epoch
        const eligible = Boolean(
            identity.accountScope &&
            identity.accountScope !== "signed-out" &&
            identity.accountScope !== "current-account" &&
            identity.generation &&
            /^[a-f0-9]{64}$/.test(identity.generation)
        )
        const key = JSON.stringify([identity.accountScope, identity.fileId, identity.generation, identity.assetId])
        for (const [expiredKey, entry] of this.entries) {
            if (entry.expiresAt <= this.now()) this.remove(expiredKey)
        }
        const cached = eligible ? this.entries.get(key) : undefined
        const assertCurrent = () => {
            if (signal?.aborted || startedEpoch !== this.epoch) {
                throw new DOMException("CAD asset request was superseded", "AbortError")
            }
        }
        try {
            assertCurrent()
            // Never return retained bytes before live authorization and generation validation.
            const response = await fetcher(cached?.etag)
            assertCurrent()
            if (response.status === 304) {
                if (!cached) {
                    throw new Error("CAD asset was revalidated without cached bytes")
                }
                // A concurrent validation failure or an LRU eviction can remove this entry
                // during validation. Its leased snapshot still belongs to this request;
                // do not reinsert it or touch a newer response for the same identity.
                if (this.entries.get(key) === cached) {
                    this.entries.delete(key)
                    cached.expiresAt = this.now() + this.ttlMs
                    this.entries.set(key, cached)
                }
                // The preparation worker transfers its input. Give each consumer independent ownership.
                return cached.bytes.slice(0)
            }
            if (response.status !== 200 || !(response.bytes instanceof ArrayBuffer)) {
                throw new Error(`CAD asset request failed (${response.status})`)
            }
            this.remove(key)
            if (
                eligible &&
                response.etag?.trim() &&
                response.bytes.byteLength <= this.maxBytes &&
                this.maxEntries > 0
            ) {
                while (
                    this.entries.size > 0 &&
                    (this.entries.size >= this.maxEntries ||
                        this.retainedBytes + response.bytes.byteLength > this.maxBytes)
                ) {
                    this.remove(this.entries.keys().next().value!)
                }
                const bytes = response.bytes.slice(0)
                this.entries.set(key, { bytes, etag: response.etag, expiresAt: this.now() + this.ttlMs })
                this.retainedBytes += bytes.byteLength
            }
            return response.bytes
        } catch (error) {
            // Cancellation rejects this consumer but retains bytes for a future authorized validation.
            // Auth, generation and network failures evict the observed entry without a stale fallback.
            if (!signal?.aborted && startedEpoch === this.epoch && this.entries.get(key) === cached) this.remove(key)
            throw error
        }
    }
}

const cache = new CadAssetCache()
export const loadCadAssetBytes = (
    identity: CadAssetIdentity,
    fetcher: (etag?: string) => Promise<CadAssetResponse>,
    signal?: AbortSignal
): Promise<ArrayBuffer> => cache.load(identity, fetcher, signal)

export const clearCadAssetCache = (): void => cache.clear()
